import {observable} from 'mobx';
import {Context, EMPTY_CONTEXT, Options, Schema} from './schema';

export type ElementsSchema<T> = Schema<T>;

export interface ListOptions<T> extends Options<Array<T | null> | null> {
  minSize?: number | ((context?: Context) => number | null);
  maxSize?: number | ((context?: Context) => number | null);
}

export class ListSchema<
  T = any,
  S extends ElementsSchema<T> = ElementsSchema<T>
> extends Schema<Array<T | null> | null, ListOptions<T>> {
  private _elementsSchema: S;

  constructor(
    // Force type parameter `T` to be inferred from the elements' schema
    elementsSchema: S & ElementsSchema<T>,
    options: ListOptions<T> = {},
  ) {
    super(options);
    this._elementsSchema = elementsSchema;
  }

  public toJSON(): object {
    return {
      type: 'ListSchema',
      ...super.toJSON(),
      minSize: this._options.minSize,
      maxSize: this._options.maxSize,
      elementsSchema: this._elementsSchema,
    };
  }

  public get elementsSchema(): S {
    return this._elementsSchema;
  }

  // When defined, merge each of schema's initial value elements with their
  // respective initial value
  public initialValue(
    context: Context = EMPTY_CONTEXT,
  ): Array<T | null> | null {
    const schemaInitialValue = super.initialValue(context);
    return schemaInitialValue === null
      ? null
      : // The initial value of a list's element may access the initial value
        // of previous elements (we use `reduce` instead of `map` for this)
        schemaInitialValue.reduce((list: Array<T | null>, element, i) => {
          list.push(
            this._merge(this.elementInitialValue(list, i, context), element),
          );
          return list;
        }, []);
  }

  // Initial value for element with index `index` of `value`
  public elementInitialValue(
    value: Array<T | null>,
    index: number,
    context: Context = EMPTY_CONTEXT,
  ): T | null {
    return this._elementsSchema.initialValue(
      this._buildChildContext(value, index, context),
    ) as T;
  }

  public minSize(context: Context = EMPTY_CONTEXT): number | null {
    return this._options.minSize === undefined
      ? null
      : typeof this._options.minSize === 'function'
        ? this._options.minSize(context)
        : this._options.minSize;
  }

  public maxSize(context: Context = EMPTY_CONTEXT): number | null {
    return this._options.maxSize === undefined
      ? null
      : typeof this._options.maxSize === 'function'
        ? this._options.maxSize(context)
        : this._options.maxSize;
  }

  public valueToMobx(
    value: Array<T | null> | null,
    context: Context = EMPTY_CONTEXT,
  ): Array<T | null> | null {
    const list = this._nullifyValue(value);
    if (list === null) {
      return null;
    }
    if (this._elementsSchema.isComputed) {
      throw new Error(
        'Computed values not supported as direct children of lists',
      );
    }
    // Return a MobX array
    const mobxList: any = observable.shallowArray();
    mobxList.replace(
      list.map((element, i) =>
        this._elementsSchema.valueToMobx(
          element,
          this._buildChildContext(mobxList, i, context),
        ),
      ),
    );
    return mobxList;
  }

  public elementToMobx(
    value: Array<T | null> | null,
    index: number,
    elementValue: any,
    context: Context = EMPTY_CONTEXT,
  ): any {
    return this._elementsSchema.valueToMobx(
      elementValue,
      this._buildChildContext(value, index, context),
    );
  }

  public valueToJS(
    value: Array<T | null> | null,
    context: Context = EMPTY_CONTEXT,
  ): Array<T | null> | null {
    const list = this.isComputed
      ? this.computedValue(context)
      : this._nullifyValue(value);
    if (list === null) {
      return null;
    }
    // When all elements are computed, return a lazy list with "getters" for
    // each element
    if (this._elementsSchema.isComputed) {
      const lazyList: Array<T | null> = [];
      list.forEach((_, i) =>
        Object.defineProperty(lazyList, `${i}`, {
          enumerable: true,
          configurable: true,
          get: () =>
            this._elementsSchema.valueToJS(
              null,
              this._buildChildContext(lazyList, i, context),
            ),
        }),
      );
      return lazyList;
    }
    // Return a copy of the list with `valueToJS` called for each element
    return list.reduce((copyList: Array<T | null>, element, i) => {
      copyList.push(
        this._elementsSchema.valueToJS(
          element,
          this._buildChildContext(copyList, i, context),
        ),
      );
      return copyList;
    }, []);
  }

  public typeIsValid(
    value: any,
    strict?: boolean,
  ): value is Array<T | null> | null {
    return (
      value === null ||
      (Array.isArray(value) &&
        value.every(element =>
          this._elementsSchema.typeIsValid(element, strict),
        ))
    );
  }

  public valueIsValid(
    value: Array<T | null> | null,
    context: Context = EMPTY_CONTEXT,
  ): boolean {
    return (
      this.requiredIsValid(value, context) &&
      this.minSizeIsValid(value, context) &&
      this.maxSizeIsValid(value, context) &&
      this.elementsAreValid(value, context) &&
      this.customFunctionIsValid(value, context)
    );
  }

  public minSizeIsValid(
    value: Array<T | null> | null,
    context: Context = EMPTY_CONTEXT,
  ): boolean {
    const minSize = this.minSize(context);
    return value === null || minSize === null || value.length >= minSize;
  }

  public maxSizeIsValid(
    value: Array<T | null> | null,
    context: Context = EMPTY_CONTEXT,
  ): boolean {
    const maxSize = this.maxSize(context);
    return value === null || maxSize === null || value.length <= maxSize;
  }

  public elementIsValid(
    value: Array<T | null>,
    index: number,
    context: Context = EMPTY_CONTEXT,
  ): boolean {
    return this._elementsSchema.valueIsValid(
      value[index],
      this._buildChildContext(value, index, context),
    );
  }

  public elementsAreValid(
    value: Array<T | null> | null,
    context: Context = EMPTY_CONTEXT,
  ): boolean {
    return (
      value === null ||
      value.every((_, i) => this.elementIsValid(value, i, context))
    );
  }
}
